import React from "react";
import { motion } from "framer-motion";
import ScrollAnimation from "../../hooks/ScrollAnimation";
import UseScrollAnimation from "../../hooks/UseScrollAnimation";
import { useTranslation } from "react-i18next";

export default function ContactHeroSection() {
  const { t } = useTranslation();
  const stats = t("contact_page.hero.stats", { returnObjects: true });
  UseScrollAnimation();

  return (
    <section className="relative isolate overflow-hidden text-white py-24 md:py-32 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700">
      <div className="absolute inset-0 -z-30 animate-[gradientMove_16s_ease_infinite] bg-[linear-gradient(120deg,#1e3a8a_0%,#1d4ed8_50%,#2563eb_100%)] bg-[length:200%_200%] opacity-95" />
      <div className="pointer-events-none absolute inset-0 -z-20">
        <span className="ball absolute left-[8%] top-[18%]" />
        <span className="ball absolute right-[12%] top-[28%] delay-300" />
        <span className="ball-sm absolute left-[16%] bottom-[20%] delay-700" />
        <span className="ball-sm absolute right-[24%] bottom-[26%] delay-500" />
        <span className="ball-sm absolute left-[32%] top-[36%] delay-1000" />
      </div>
      <div
        aria-hidden
        className="absolute -top-40 -left-40 h-[36rem] w-[36rem] rounded-full bg-white/10 blur-3xl -z-10"
        style={{
          maskImage: "radial-gradient(closest-side, white, transparent)",
        }}
      />
      <div
        aria-hidden
        className="absolute -bottom-32 -right-32 h-[34rem] w-[34rem] rounded-full bg-white/10 blur-3xl -z-10"
        style={{
          maskImage: "radial-gradient(closest-side, white, transparent)",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollAnimation direction="up" delay={0.1}>
          <div className="text-center">
            <motion.div
              className="inline-flex items-center gap-2 mb-5 rounded-full border border-white/20 bg-white/10 px-5 py-2 backdrop-blur-md"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-[pingSlow_2.4s_linear_infinite]" />
              <span className="text-sm font-semibold text-white/90">
                {t("contact_page.hero.badge")}
              </span>
            </motion.div>

            <motion.h1
              className="text-5xl md:text-6xl font-extrabold tracking-tight mb-4 drop-shadow-[0_6px_24px_rgba(0,0,0,.35)]"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              style={{
                background:
                  "linear-gradient(180deg, #ffffff 0%, #e2e8f0 60%, rgba(255,255,255,.9) 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {t("contact_page.hero.title")}
            </motion.h1>

            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.45, duration: 0.7 }}
              className="relative mx-auto mt-4 h-[3px] w-48 origin-center rounded-full bg-gradient-to-r from-cyan-300 via-white to-blue-300 shadow-[0_0_18px_rgba(255,255,255,.6)] overflow-hidden"
            >
              <div className="absolute inset-0 shimmer opacity-80" />
              <div className="absolute -inset-1 bg-white/40 blur-md glowPulse rounded-full" />
            </motion.div>

            <motion.h2
              className="text-2xl md:text-3xl font-semibold mb-6 text-blue-200"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            >
              {t("contact_page.hero.subtitle")}
            </motion.h2>

            <motion.p
              className="text-xl text-blue-100 max-w-3xl mx-auto mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
            >
              {t("contact_page.hero.description")}
            </motion.p>

            <motion.div
              className="flex flex-wrap justify-center gap-4 text-sm"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.6 }}
            >
              {Array.isArray(stats) && stats.map((stat, index) => (
                <div key={index} className="bg-white/10 border border-white/15 backdrop-blur-sm rounded-full px-4 py-2">
                  <span className="font-semibold">{stat}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </ScrollAnimation>
      </div>

      <div className="absolute bottom-0 left-0 right-0 overflow-hidden leading-none -z-10">
        <svg
          viewBox="0 0 1440 240"
          className="w-full h-[100px] md:h-[140px]"
          preserveAspectRatio="none"
        >
          <path
            className="animate-[waveDrift_14s_ease_in_out_infinite]"
            fill="rgba(255,255,255,0.20)"
            d="M0,140 C180,160 360,120 540,140 C720,160 900,200 1080,170 C1260,140 1350,120 1440,130 L1440,240 L0,240 Z"
          />
          <path
            className="animate-[waveDrift_12s_ease_in_out_infinite_reverse]"
            fill="rgba(255,255,255,0.28)"
            d="M0,150 C200,130 420,170 640,155 C860,140 1000,150 1220,160 C1330,165 1385,165 1440,166 L1440,240 L0,240 Z"
          />
          <path
            className="animate-[waveDrift_10s_ease_in_out_infinite]"
            fill="rgba(255,255,255,0.36)"
            d="M0,165 C240,170 420,160 620,168 C820,176 1040,154 1240,162 C1340,166 1390,166 1440,166 L1440,240 L0,240 Z"
          />
        </svg>
      </div>
    </section>
  );
}
